"use client";

import { useEffect, useState } from "react";
import React from "react";

import { useNavigation } from "@refinedev/core";
import {
  Calendar,
  MapPin,
  Mic2Icon,
  Music,
  User,
  Users,
  XIcon,
} from "lucide-react";
import moment from "moment";

import { UserAvatar } from "@/components/common/user-avatar";
import { CloneButton } from "@/components/refine-ui/buttons/clone";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMembers } from "@/contexts/member-context";
import { stringToBadgeClass } from "@/lib/colorUtils";
import type { IEvent } from "@/types/event";

interface RowPreviewProps {
  data: IEvent | null;
}

export function RowPreview({ data }: RowPreviewProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { members } = useMembers();

  const member = React.useMemo(
    () => members.find((x) => x.id === data?.createdBy),
    [data?.createdBy, members],
  );

  useEffect(() => {
    if (data) {
      // Small delay to create smooth transition
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [data]);

  if (!data) return <></>;

  return (
    <div
      className="fixed top-4 right-4 z-50 transition-all duration-200 ease-out backdrop-blur-3xl bg-background/10"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(-8px)",
      }}
    >
      <Card className="w-xl shadow-lg border-2 p-4 bg-background/20">
        {(data.media?.photos?.length ?? 0) > 0 && (
          <div className="relative aspect-square overflow-hidden rounded-t-lg">
            <img
              src={data.media?.photos?.[0]?.url || "/placeholder.svg"}
              alt={data.name}
              className="object-cover"
            />
          </div>
        )}

        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg leading-tight">{data.name}</CardTitle>
            <Badge variant="outline" className={stringToBadgeClass(data.type)}>
              {data.type}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div>
            <Badge
              variant="outline"
              className={stringToBadgeClass(data.status)}
            >
              {data.status}
            </Badge>
          </div>

          <div className="flex items-start gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div>
              <span className="text-muted-foreground">Start Date: </span>
              <span className="font-medium">
                {new Date(data.startDate * 1000).toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
          {data.endDate && (
            <div className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <span className="text-muted-foreground">End Date: </span>
                <span className="font-medium">
                  {new Date(data.endDate * 1000).toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
            {data.venue && (
              <div>
                <div className="font-medium">{data.venue.name}</div>
                <div className="text-muted-foreground text-xs">
                  {(data.venue as any).address} • {data.geodata.name},{" "}
                  {data.geodata.country}
                </div>
              </div>
            )}
            {!data.venue && data.venueTba && (
              <div>
                <div className="font-medium">Venue TBA</div>
                <div className="text-muted-foreground text-xs">
                  {data.geodata?.name}
                  {data.geodata?.country && `, ${data.geodata.country}`}
                </div>
              </div>
            )}
            {data.mapsUrl && (
              <div>
                <div className="font-medium">{data.mapsUrl}</div>
              </div>
            )}
          </div>

          {(data.genres?.length ?? 0) > 0 && (
            <div className="flex items-start gap-2 ">
              <Music className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex flex-wrap gap-1">
                {data.genres?.map((genre) => (
                  <Badge key={genre.id} variant="secondary" className="text-xs">
                    {genre.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {data.lineup && data.lineup.length > 0 && (
            <div className="flex items-start gap-2 ">
              <Mic2Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex flex-wrap gap-1">
                {data.lineup.map((l) => (
                  <Badge
                    key={l.id ?? l.name}
                    variant="secondary"
                    className="text-xs"
                  >
                    {l.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2  pt-2 border-t">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Interested:</span>
            <span className="font-medium">
              {data.interestedIn.toLocaleString()}
            </span>
          </div>
          <div className="space-y-2 w-1/2">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Created At</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm">
                {moment(data?.createdAt).format("DD/MM/YYYY HH:mm") ?? ""}
              </span>
            </div>
          </div>
          <div className="space-y-2 w-1/2">
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Created By</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <UserAvatar {...member} half />
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <EditButton resource="events" recordItemId={data.id} newTab />
            <CloneButton resource="events" recordItemId={data.id} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
